import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { toast } from "sonner";
import { Save, FileDown, ShieldCheck, ShieldAlert, Repeat, Search, ClipboardCheck, PenLine, FileCheck } from "lucide-react";
import { DocumentImagesGallery } from "../../components/DocumentImagesGallery";
import { ArticlePicker } from "../../components/ArticlePicker";
import { DiagnosticTestsEditor } from "../../components/DiagnosticTestsEditor";
import { UnlockPatternPicker } from "../../components/UnlockPatternPicker";
import { PhoneDamageMarker } from "../../components/PhoneDamageMarker";
import { SignaturePad } from "../../components/SignaturePad";

const TYPE_ORIGINE_LABELS = { ticket: "Vente", intervention: "Intervention", reprise: "Reprise", libre: "Saisie libre" };

export default function SAVFormPage() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [data, setData] = useState(() => {
    // Arrivée depuis Intervention (bouton "Créer un SAV") : pré-remplissage
    // direct sans passer par la recherche manuelle du document d'origine.
    const depuisIntervention = location.state?.from_intervention;
    if (depuisIntervention) {
      return {
        type_origine: "intervention", document_origine_id: depuisIntervention.id,
        document_origine_numero: depuisIntervention.numero,
        client_id: null, client_nom: depuisIntervention.client_nom || "",
        client_tel: depuisIntervention.client_tel || "", client_email: depuisIntervention.client_email || "",
        produit_nom: depuisIntervention.produit_nom || "", imei: depuisIntervention.imei || "",
        date_achat: (depuisIntervention.date_achat || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
        garantie_mois: 12, motif: depuisIntervention.motif || "",
        statut: "recu", statut_historique: [], notes_internes: "",
        remarque: "", type_dossier: "reparation", tests_entree: {}, tests_sortie: {},
        deverrouillage_type: null, deverrouillage_pin: "", deverrouillage_mot_de_passe: "", deverrouillage_schema: null,
        schema_avant_marques: [], schema_arriere_marques: [],
      };
    }
    return {
      type_origine: "libre", document_origine_id: null, document_origine_numero: null,
      client_id: null, client_nom: "", client_tel: "", client_email: "",
      produit_nom: "", imei: "", date_achat: new Date().toISOString().slice(0, 10),
      garantie_mois: 12, motif: "", statut: "recu", statut_historique: [], notes_internes: "",
      remarque: "", type_dossier: "reparation", tests_entree: {}, tests_sortie: {},
      deverrouillage_type: null, deverrouillage_pin: "", deverrouillage_mot_de_passe: "", deverrouillage_schema: null,
      schema_avant_marques: [], schema_arriere_marques: [],
    };
  });
  const [statuts, setStatuts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  const [rechercheOpen, setRechercheOpen] = useState(false);
  const [rechercheQuery, setRechercheQuery] = useState("");
  const [rechercheResultats, setRechercheResultats] = useState([]);
  const [recherchLoading, setRecherchLoading] = useState(false);

  const [articles, setArticles] = useState([]);
  const [stagedImages, setStagedImages] = useState([]);
  const [testsEntreeOpen, setTestsEntreeOpen] = useState(false);
  const [testsSortieOpen, setTestsSortieOpen] = useState(false);
  const [signerOpen, setSignerOpen] = useState(false);
  const [signerSaving, setSignerSaving] = useState(false);
  const sigRef = useRef(null);
  const [documentsSignes, setDocumentsSignes] = useState([]);
  const [documentsSignesOpen, setDocumentsSignesOpen] = useState(false);
  const [echangeOpen, setEchangeOpen] = useState(false);
  const [echangeArticleId, setEchangeArticleId] = useState("");
  const [echangeSaving, setEchangeSaving] = useState(false);

  const canEdit = isNew ? hasPerm(user, "sav", "create") : hasPerm(user, "sav", "edit");

  useEffect(() => {
    api.get("/sav/statuts").then((r) => setStatuts(r.data)).catch(() => {});
    api.get("/stock").then((r) => setArticles(r.data)).catch(() => {});
    if (!isNew) {
      setLoading(true);
      api.get(`/sav/${id}`).then((r) => { setData(r.data); setLoading(false); }).catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail));
        setLoading(false);
      });
    }
  }, [id, isNew]);

  const rechercherOrigine = async (query) => {
    setRechercheQuery(query);
    if (!query.trim()) { setRechercheResultats([]); return; }
    setRecherchLoading(true);
    try {
      const { data: res } = await api.get(`/sav/search-origine/${data.type_origine}`, { params: { q: query } });
      setRechercheResultats(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRecherchLoading(false);
    }
  };

  const [articleChoixOpen, setArticleChoixOpen] = useState(false);
  const [articleChoixOptions, setArticleChoixOptions] = useState([]);
  const [articleChoixPrefill, setArticleChoixPrefill] = useState(null);
  const [articleChoixDocId, setArticleChoixDocId] = useState(null);

  const selectionnerOrigine = async (doc) => {
    try {
      const { data: prefill } = await api.get(`/sav/lookup/${data.type_origine}/${doc.id}`);
      const articlesVendus = prefill.articles_vendus || [];
      if (articlesVendus.length > 1) {
        // Plusieurs articles sur cette vente : on demande explicitement
        // lequel est concerné par le SAV, plutôt que de tous les regrouper.
        setArticleChoixPrefill(prefill);
        setArticleChoixDocId(doc.id);
        setArticleChoixOptions(articlesVendus);
        setArticleChoixOpen(true);
        return;
      }
      appliquerPrefill(prefill, doc.id, articlesVendus[0] || prefill.produit_nom);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const appliquerPrefill = (prefill, documentId, produitNom) => {
    setData((prev) => ({
      ...prev,
      document_origine_id: documentId, document_origine_numero: prefill.document_origine_numero,
      client_id: prefill.client_id, client_nom: prefill.client_nom, client_tel: prefill.client_tel,
      client_email: prefill.client_email, produit_nom: produitNom, imei: prefill.imei,
      date_achat: (prefill.date_achat || "").slice(0, 10) || prev.date_achat,
    }));
    setRechercheOpen(false);
    setRechercheQuery("");
    setRechercheResultats([]);
    setArticleChoixOpen(false);
    setArticleChoixOptions([]);
    setArticleChoixPrefill(null);
    setArticleChoixDocId(null);
    toast.success("Document lié — informations pré-remplies");
  };

  const choisirArticle = (nom) => {
    appliquerPrefill(articleChoixPrefill, articleChoixDocId, nom);
  };

  const save = async () => {
    if (!data.client_nom.trim() || !data.produit_nom.trim() || !data.motif.trim()) {
      toast.error("Client, produit et motif sont obligatoires");
      return;
    }
    if (data.type_origine !== "libre" && !data.document_origine_id) {
      toast.error("Sélectionnez le document d'origine (ou passez en saisie libre)");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data: created } = await api.post("/sav", {
          type_origine: data.type_origine, document_origine_id: data.document_origine_id,
          document_origine_numero: data.document_origine_numero,
          client_id: data.client_id, client_nom: data.client_nom, client_tel: data.client_tel, client_email: data.client_email,
          produit_nom: data.produit_nom, imei: data.imei,
          date_achat: new Date(data.date_achat).toISOString(), garantie_mois: Number(data.garantie_mois),
          motif: data.motif, remarque: data.remarque, type_dossier: data.type_dossier,
          tests_entree: data.tests_entree, tests_sortie: data.tests_sortie,
          deverrouillage_type: data.deverrouillage_type, deverrouillage_pin: data.deverrouillage_pin,
          deverrouillage_mot_de_passe: data.deverrouillage_mot_de_passe, deverrouillage_schema: data.deverrouillage_schema,
          schema_avant_marques: data.schema_avant_marques, schema_arriere_marques: data.schema_arriere_marques,
        });
        toast.success("Dossier SAV créé");
        navigate(`/sav/${created.id}`, { replace: true });
      } else {
        const { data: updated } = await api.patch(`/sav/${id}`, {
          client_nom: data.client_nom, client_tel: data.client_tel, client_email: data.client_email,
          produit_nom: data.produit_nom, imei: data.imei, motif: data.motif,
          garantie_mois: Number(data.garantie_mois), notes_internes: data.notes_internes,
          remarque: data.remarque, type_dossier: data.type_dossier,
          tests_entree: data.tests_entree, tests_sortie: data.tests_sortie,
          deverrouillage_type: data.deverrouillage_type, deverrouillage_pin: data.deverrouillage_pin,
          deverrouillage_mot_de_passe: data.deverrouillage_mot_de_passe, deverrouillage_schema: data.deverrouillage_schema,
          schema_avant_marques: data.schema_avant_marques, schema_arriere_marques: data.schema_arriere_marques,
        });
        setData(updated);
        toast.success("Dossier SAV enregistré");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changerStatut = async (nouveauStatut) => {
    try {
      const { data: updated } = await api.patch(`/sav/${id}`, { statut: nouveauStatut });
      setData(updated);
      toast.success("Statut mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const confirmerEchange = async () => {
    if (!echangeArticleId) { toast.error("Choisissez un article de remplacement"); return; }
    setEchangeSaving(true);
    try {
      const { data: updated } = await api.post(`/sav/${id}/echange-standard`, { article_remplacement_id: echangeArticleId });
      setData(updated);
      toast.success("Échange standard effectué — stock mis à jour");
      setEchangeOpen(false);
      setEchangeArticleId("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEchangeSaving(false);
    }
  };

  const validerSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Veuillez signer avant de valider");
      return;
    }
    setSignerSaving(true);
    try {
      await api.post(`/sav/${id}/signer`, { signature_data: sigRef.current.toDataURL() });
      toast.success(`Document signé et archivé (statut : ${statuts.find((s) => s.key === data.statut)?.label || data.statut})`);
      setSignerOpen(false);
      sigRef.current.clear();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSignerSaving(false);
    }
  };

  const ouvrirDocumentsSignes = async () => {
    setDocumentsSignesOpen(true);
    try {
      const { data: docs } = await api.get(`/sav/${id}/documents-signes`);
      setDocumentsSignes(docs);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <Layout title="SAV"><p className="text-sm text-muted-foreground">Chargement...</p></Layout>;

  return (
    <Layout title={isNew ? "Nouveau dossier SAV" : `Dossier SAV ${data.numero || ""}`}>
      <div className="max-w-3xl space-y-6">
        {!isNew && (
          <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
            <div>
              <p className="font-semibold">{data.numero}</p>
              <p className="text-xs text-muted-foreground">Créé le {new Date(data.created_at).toLocaleDateString("fr-FR")}</p>
            </div>
            <a href={`${api.defaults.baseURL}/sav/${id}/pdf`} target="_blank" rel="noreferrer" data-testid="sav-pdf-link">
              <Button variant="outline" size="sm" className="gap-2"><FileDown size={14} /> PDF</Button>
            </a>
          </div>
        )}

        {isNew && (
          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <label className="text-sm font-semibold block">Document d'origine</label>
            <Select value={data.type_origine} onValueChange={(v) => setData({ ...data, type_origine: v, document_origine_id: null, document_origine_numero: null })}>
              <SelectTrigger data-testid="sav-type-origine"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_ORIGINE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {data.type_origine !== "libre" && (
              <div>
                {data.document_origine_numero ? (
                  <div className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2 text-sm">
                    <span>Lié à : <span className="font-medium">{data.document_origine_numero}</span></span>
                    <button type="button" className="text-primary text-xs" onClick={() => setRechercheOpen(true)} data-testid="sav-changer-origine">Changer</button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setRechercheOpen(true)} data-testid="sav-rechercher-origine">
                    <Search size={14} /> Rechercher le document ({TYPE_ORIGINE_LABELS[data.type_origine].toLowerCase()})
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <label className="text-sm font-semibold block">Client</label>
          <Input placeholder="Nom du client" value={data.client_nom} onChange={(e) => setData({ ...data, client_nom: e.target.value })} disabled={!canEdit} data-testid="sav-client-nom" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Téléphone" value={data.client_tel} onChange={(e) => setData({ ...data, client_tel: e.target.value })} disabled={!canEdit} data-testid="sav-client-tel" />
            <Input placeholder="Email" value={data.client_email} onChange={(e) => setData({ ...data, client_email: e.target.value })} disabled={!canEdit} data-testid="sav-client-email" />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <label className="text-sm font-semibold block">Produit</label>
          <Input placeholder="Produit concerné" value={data.produit_nom} onChange={(e) => setData({ ...data, produit_nom: e.target.value })} disabled={!canEdit} data-testid="sav-produit-nom" />
          <Input placeholder="IMEI (optionnel)" value={data.imei} onChange={(e) => setData({ ...data, imei: e.target.value })} disabled={!canEdit} data-testid="sav-imei" />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Date d'achat / d'intervention</label>
              <Input type="date" value={data.date_achat} onChange={(e) => setData({ ...data, date_achat: e.target.value })} disabled={!canEdit || !isNew} data-testid="sav-date-achat" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Garantie (mois)</label>
              <Input type="number" min="0" value={data.garantie_mois} onChange={(e) => setData({ ...data, garantie_mois: e.target.value })} disabled={!canEdit} data-testid="sav-garantie-mois" />
            </div>
          </div>
          {!isNew && data.sous_garantie != null && (
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${data.sous_garantie ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-destructive/10 text-destructive"}`} data-testid="sav-garantie-info">
              {data.sous_garantie ? <ShieldCheck size={15} /> : <ShieldAlert size={15} />}
              {data.sous_garantie ? `Sous garantie — ${data.garantie_jours_restants} jour(s) restant(s)` : "Garantie expirée"}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-5 space-y-2">
          <label className="text-sm font-semibold block">Motif</label>
          <Textarea value={data.motif} onChange={(e) => setData({ ...data, motif: e.target.value })} disabled={!canEdit} className="h-24" data-testid="sav-motif" />
        </div>

        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <label className="text-sm font-semibold block">Type de dossier</label>
          <Select value={data.type_dossier} onValueChange={(v) => setData({ ...data, type_dossier: v })} disabled={!canEdit}>
            <SelectTrigger data-testid="sav-type-dossier"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="reparation">Réparation</SelectItem>
              <SelectItem value="vente">Vente</SelectItem>
            </SelectContent>
          </Select>

          <label className="text-sm font-semibold block pt-1">Remarque</label>
          <Textarea
            value={data.remarque} onChange={(e) => setData({ ...data, remarque: e.target.value })}
            disabled={!canEdit} className="h-16" placeholder="Observation complémentaire, distincte du motif..."
            data-testid="sav-remarque"
          />

          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setTestsEntreeOpen(true)} data-testid="sav-tests-entree-button">
              <ClipboardCheck size={14} /> Tests d'entrée
              {Object.keys(data.tests_entree || {}).length > 0 && (
                <span className="text-xs text-muted-foreground">({Object.keys(data.tests_entree).length})</span>
              )}
            </Button>
            <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setTestsSortieOpen(true)} data-testid="sav-tests-sortie-button">
              <ClipboardCheck size={14} /> Tests de sortie
              {Object.keys(data.tests_sortie || {}).length > 0 && (
                <span className="text-xs text-muted-foreground">({Object.keys(data.tests_sortie).length})</span>
              )}
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <label className="text-sm font-semibold block">Déverrouillage de l'appareil</label>
          <p className="text-xs text-muted-foreground -mt-2">Information sensible, visible uniquement par les personnes ayant accès au SAV.</p>
          <Select
            value={data.deverrouillage_type || "aucun"}
            onValueChange={(v) => setData({ ...data, deverrouillage_type: v === "aucun" ? null : v })}
            disabled={!canEdit}
          >
            <SelectTrigger data-testid="sav-deverrouillage-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="aucun">Aucun</SelectItem>
              <SelectItem value="pin">Code PIN</SelectItem>
              <SelectItem value="mot_de_passe">Mot de passe</SelectItem>
              <SelectItem value="schema">Schéma (motif à 9 points)</SelectItem>
            </SelectContent>
          </Select>
          {data.deverrouillage_type === "pin" && (
            <Input
              type="text" inputMode="numeric" placeholder="Code PIN" value={data.deverrouillage_pin}
              onChange={(e) => setData({ ...data, deverrouillage_pin: e.target.value })} disabled={!canEdit}
              data-testid="sav-deverrouillage-pin"
            />
          )}
          {data.deverrouillage_type === "mot_de_passe" && (
            <Input
              type="text" placeholder="Mot de passe" value={data.deverrouillage_mot_de_passe}
              onChange={(e) => setData({ ...data, deverrouillage_mot_de_passe: e.target.value })} disabled={!canEdit}
              data-testid="sav-deverrouillage-mdp"
            />
          )}
          {data.deverrouillage_type === "schema" && (
            <div className="flex justify-center pt-2">
              <UnlockPatternPicker
                value={data.deverrouillage_schema} disabled={!canEdit}
                onChange={(schema) => setData({ ...data, deverrouillage_schema: schema })}
              />
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <label className="text-sm font-semibold block">Schéma de l'appareil (dommages)</label>
          <p className="text-xs text-muted-foreground -mt-2">Touchez la silhouette pour marquer un dommage, touchez une marque pour la retirer.</p>
          <div className="flex justify-center gap-8 pt-2">
            <PhoneDamageMarker
              label="Avant" marques={data.schema_avant_marques} disabled={!canEdit}
              onChange={(marques) => setData({ ...data, schema_avant_marques: marques })}
            />
            <PhoneDamageMarker
              label="Arrière" marques={data.schema_arriere_marques} disabled={!canEdit}
              onChange={(marques) => setData({ ...data, schema_arriere_marques: marques })}
            />
          </div>
        </div>

        {!isNew && (
          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <label className="text-sm font-semibold block">Statut</label>
            <Select value={data.statut} onValueChange={changerStatut} disabled={!hasPerm(user, "sav", "edit")}>
              <SelectTrigger data-testid="sav-statut-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {statuts.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {hasPerm(user, "sav", "edit") && !data.echange_effectue && (
              <Button variant="outline" size="sm" className="gap-2" onClick={() => setEchangeOpen(true)} data-testid="sav-echange-button">
                <Repeat size={14} /> Échange standard
              </Button>
            )}
            {data.echange_effectue && (
              <p className="text-xs text-muted-foreground">Échange standard déjà effectué pour ce dossier.</p>
            )}
            <div className="pt-2 border-t border-border">
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Historique</p>
              <div className="space-y-1">
                {(data.statut_historique || []).map((h, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    {new Date(h.date).toLocaleString("fr-FR")} — {statuts.find((s) => s.key === h.statut)?.label || h.statut} {h.user && `(${h.user})`}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}

        {!isNew && (
          <DocumentImagesGallery module="sav" itemId={id} canEdit={hasPerm(user, "sav", "edit")} title="Photos" stagedFiles={stagedImages} onStagedFilesChange={setStagedImages} />
        )}

        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="sav-save-button">
              <Save size={15} /> {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
            {!isNew && (
              <Button variant="outline" onClick={() => setSignerOpen(true)} className="gap-2" data-testid="sav-signer-button">
                <PenLine size={15} /> Signer
              </Button>
            )}
          </div>
        )}
        {!isNew && (
          <Button variant="outline" onClick={ouvrirDocumentsSignes} className="gap-2" data-testid="sav-voir-documents-signes-button">
            <FileCheck size={15} /> Voir les documents signés
            {documentsSignes.length > 0 && (
              <span className="text-xs text-muted-foreground">({documentsSignes.length})</span>
            )}
          </Button>
        )}
      </div>

      <Dialog open={rechercheOpen} onOpenChange={setRechercheOpen}>
        <DialogContent data-testid="sav-recherche-dialog">
          <DialogHeader><DialogTitle>Rechercher un document ({TYPE_ORIGINE_LABELS[data.type_origine]?.toLowerCase()})</DialogTitle></DialogHeader>
          <Input
            placeholder="Numéro du document..." value={rechercheQuery}
            onChange={(e) => rechercherOrigine(e.target.value)} autoFocus data-testid="sav-recherche-input"
          />
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {recherchLoading && <p className="text-sm text-muted-foreground">Recherche...</p>}
            {!recherchLoading && rechercheResultats.map((doc) => (
              <button
                key={doc.id} type="button" onClick={() => selectionnerOrigine(doc)}
                className="w-full text-left rounded-lg border border-border p-2.5 hover:border-primary transition-colors"
                data-testid={`sav-recherche-result-${doc.id}`}
              >
                <p className="text-sm font-medium">{doc.numero}</p>
                <p className="text-xs text-muted-foreground">{doc.client_nom} — {doc.produit_nom}</p>
              </button>
            ))}
            {!recherchLoading && rechercheQuery && rechercheResultats.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-3">Aucun résultat.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={articleChoixOpen} onOpenChange={(v) => !v && setArticleChoixOpen(false)}>
        <DialogContent data-testid="sav-article-choix-dialog">
          <DialogHeader><DialogTitle>Quel article est concerné ?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Cette vente contient plusieurs articles — choisissez celui concerné par ce dossier SAV.
          </p>
          <div className="space-y-1.5">
            {articleChoixOptions.map((nom, i) => (
              <button
                key={i} type="button" onClick={() => choisirArticle(nom)}
                className="w-full text-left rounded-lg border border-border p-2.5 hover:border-primary transition-colors"
                data-testid={`sav-article-choix-${i}`}
              >
                {nom}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={testsEntreeOpen} onOpenChange={setTestsEntreeOpen}>
        <DialogContent data-testid="sav-tests-entree-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Tests d'entrée</DialogTitle></DialogHeader>
          <DiagnosticTestsEditor
            tests={data.tests_entree} disabled={!canEdit}
            onChange={(tests) => setData({ ...data, tests_entree: tests })}
          />
          <DialogFooter>
            <Button onClick={() => setTestsEntreeOpen(false)} data-testid="sav-tests-entree-fermer">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={testsSortieOpen} onOpenChange={setTestsSortieOpen}>
        <DialogContent data-testid="sav-tests-sortie-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Tests de sortie</DialogTitle></DialogHeader>
          <DiagnosticTestsEditor
            tests={data.tests_sortie} disabled={!canEdit}
            onChange={(tests) => setData({ ...data, tests_sortie: tests })}
          />
          <DialogFooter>
            <Button onClick={() => setTestsSortieOpen(false)} data-testid="sav-tests-sortie-fermer">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={echangeOpen} onOpenChange={setEchangeOpen}>
        <DialogContent data-testid="sav-echange-dialog">
          <DialogHeader><DialogTitle>Échange standard</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Choisissez l'article donné en remplacement au client — son stock sera décrémenté d'une unité.
          </p>
          <ArticlePicker articles={articles} value={echangeArticleId} onChange={setEchangeArticleId} testId="sav-echange-article" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEchangeOpen(false)}>Annuler</Button>
            <Button onClick={confirmerEchange} disabled={echangeSaving} data-testid="sav-echange-confirm">
              {echangeSaving ? "En cours..." : "Confirmer l'échange"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={signerOpen} onOpenChange={(v) => { setSignerOpen(v); if (!v) sigRef.current?.clear(); }}>
        <DialogContent data-testid="sav-signer-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Signature — statut actuel : {statuts.find((s) => s.key === data.statut)?.label || data.statut}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Faites lire le document au client, puis faites-le signer ci-dessous. Le document (avec la signature) sera archivé et restera consultable via « Voir les documents signés ».
          </p>
          <div className="rounded-lg border border-border overflow-hidden h-[45vh]">
            <iframe
              src={`${api.defaults.baseURL}/sav/${id}/pdf`} title="Aperçu du bon SAV"
              className="w-full h-full" data-testid="sav-signer-pdf-preview"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Signature du client</label>
            <SignaturePad ref={sigRef} testId="sav-signature-pad" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSignerOpen(false)}>Annuler</Button>
            <Button onClick={validerSignature} disabled={signerSaving} className="gap-2" data-testid="sav-signer-valider">
              <PenLine size={15} /> {signerSaving ? "Enregistrement..." : "Valider la signature"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={documentsSignesOpen} onOpenChange={setDocumentsSignesOpen}>
        <DialogContent data-testid="sav-documents-signes-dialog">
          <DialogHeader><DialogTitle>Documents signés</DialogTitle></DialogHeader>
          {documentsSignes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="sav-documents-signes-vide">
              Aucun document signé pour l'instant.
            </p>
          ) : (
            <div className="space-y-2">
              {documentsSignes.map((doc) => (
                <a
                  key={doc.id}
                  href={`${api.defaults.baseURL}/sav/${id}/documents-signes/${doc.id}/pdf`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center justify-between rounded-lg border border-border p-3 hover:border-primary transition-colors"
                  data-testid={`sav-document-signe-${doc.id}`}
                >
                  <div>
                    <p className="text-sm font-medium">Statut : {doc.statut_label}</p>
                    <p className="text-xs text-muted-foreground">
                      Signé le {new Date(doc.date).toLocaleString("fr-FR")} {doc.user && `par ${doc.user}`}
                    </p>
                  </div>
                  <FileDown size={16} className="text-primary shrink-0" />
                </a>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
